import Vue from "vue";
import {Mixin} from "vue-mixin-decorator";
import {Route} from "vue-router";

/**
 * パス情報
 */
export class PathInfo {
    /**
     * ルート名のパターン
     * /アクション名/第二カテゴリ/第一カテゴリ/コンポーネント名
     */
    private static ROUTE_NAME_PATTERN = /^\/(?<action>[^/]+)\/(?<category2nd>[^/]+)\/(?<category1st>[^/]+)\/(?<component>[^/]+).*$/;

    /**
     * ルートからパス情報を生成します
     *
     * @param route ルート
     */
    public static routeOf(route: Route) {
        return new PathInfo(route);
    }

    /** アクション */
    public readonly action: string | null;

    /** 第一カテゴリ */
    public readonly category1st: string | null;

    /** 第二カテゴリ */
    public readonly category2nd: string | null;

    /** コンポーネント */
    public readonly component: string | null;

    /**
     * コンストラクタ
     *
     * @param route ルート
     * @private
     */
    private constructor(route: Route) {
        const name = route.name;
        if (name) {
            const m = PathInfo.ROUTE_NAME_PATTERN.exec(name);
            this.action = m?.groups?.action || null;
            this.category1st = m?.groups?.category1st || null;
            this.category2nd = m?.groups?.category2nd || null;
            this.component = m?.groups?.component || null;
        } else {
            this.action = null;
            this.category1st = null;
            this.category2nd = null;
            this.component = null;
        }
    }

    /**
     * ページが編集可能かどうかを取得します
     */
    public isEditable() {
        return this.action === 'edit';
    }

    /**
     * アクションが指定された文字列かどうかを取得します
     *
     * @param name 試験を行うアクション名
     */
    isAction(name: string): boolean {
        return this.action === name;
    }

    /**
     * 第1カテゴリが指定された文字列かどうかを取得します
     *
     * @param name 試験を行う第1カテゴリ名
     */
    isCategory1st(name: string): boolean {
        return this.category1st === name;
    }

    /**
     * 第2カテゴリが指定された文字列かどうかを取得します
     *
     * @param name 試験を行う第2カテゴリ名
     */
    isCategory2nd(name: string): boolean {
        return this.category2nd === name;
    }

    /**
     * 現在表示しているページのメタ情報の一部を変更したルート名を生成します
     *
     * @param componentName コンポーネント名
     * @param action アクション
     */
    alterRouteName(componentName: string, action?: 'browse' | 'edit') {
        const _action = action || this.action;
        const _category2nd = this.category2nd;
        const _category1st = this.category1st;
        if (_action && _category2nd && _category1st) {
            const m = componentName.match(/^\.(\/.*)$/);
            if(m) {
                componentName = `${this.component}${m[1]}`;
            }
            return `/${_action}/${_category2nd}/${_category1st}/${componentName}`;
        }
        return componentName;
    }
}

/**
 * ページのパス情報に関するユーティリティ
 */
@Mixin
export default class PathInfoMixin extends Vue {
    /**
     * パス情報を取得します
     */
    get pathInfo(): PathInfo {
        return PathInfo.routeOf(this.$route);
    }

    /**
     * ページが編集可能かどうかを取得します
     */
    get isEditable(): boolean {
        return this.pathInfo.isEditable();
    }

    /**
     * アクションを取得します
     */
    get action(): string | null {
        return this.pathInfo.action;
    }

    /**
     * 第1カテゴリを取得します
     */
    get category1st(): string | null {
        return this.pathInfo.category1st;
    }

    /**
     * 第2カテゴリを取得します
     */
    get category2nd(): string | null {
        return this.pathInfo.category2nd;
    }

    /**
     * 現在表示しているページのメタ情報の一部を変更したルート名を生成します
     *
     * @param componentName コンポーネント名
     * @param action アクション
     */
    alterRouteName(componentName: string, action?: 'browse' | 'edit') {
        return this.pathInfo.alterRouteName(componentName, action);
    }

    /**
     * アクションが指定された文字列かどうかを取得します
     *
     * @param name 試験を行うアクション名
     */
    isAction(name: string): boolean {
        return this.pathInfo.isAction(name);
    }

    /**
     * 第1カテゴリが指定された文字列かどうかを取得します
     *
     * @param name 試験を行う第1カテゴリ名
     */
    isCategory1st(name: string): boolean {
        return this.pathInfo.isCategory1st(name);
    }

    /**
     * 第2カテゴリが指定された文字列かどうかを取得します
     *
     * @param name 試験を行う第2カテゴリ名
     */
    isCategory2nd(name: string): boolean {
        return this.pathInfo.isCategory2nd(name);
    }
}
